// Adapted from OpenCharts' DrawingToolsManager (MIT, see LICENSE-OpenCharts).
// Changes: lightweight-charts v5, pointer limited to the price pane (the RSI
// pane sits under it), one change event per gesture (one undo step), keyboard
// shortcuts only for the chart last clicked, Escape marked as handled.
import type { IChartApi } from "lightweight-charts";
import { DrawingsPrimitive } from "./DrawingsPrimitive";
import { dist, type Pt, snapAngle } from "./geometry";
import { hitTest } from "./hitTest";
import { type AnySeries, makeResolveCtx, type ResolveCtx, resolveEntry, timeToX, xToTime } from "./resolve";
import {
  DEFAULT_DRAWING_COLOR, PLAN_REWARD, type DataPoint, type Drawing, type DrawingCallbacks, type DrawingChange, type DrawingTool,
  type DrawTheme, type Hit, type ResolvedEntry,
} from "./types";

// Releasing farther than this from the first anchor commits in one
// press-drag-release; otherwise a second click does (TradingView's two modes).
const DRAG_COMMIT_THRESHOLD_PX = 10;
// Magnet: snap to the nearest open/high/low/close of the bar under the cursor
// when within this many px.
const MAGNET_THRESHOLD_PX = 14;
// Snap onto another drawing's anchor within this many px.
const SNAP_ANCHOR_PX = 8;
const TOUCH_HIT_SCALE = 2;
const LONG_PRESS_MS = 500;
const LONG_PRESS_CANCEL_PX = 10;

/** Alt + key arms a tool (matched on the physical key). */
const TOOL_SHORTCUTS: Record<string, DrawingTool> = {
  KeyT: "trendline",
  KeyH: "horizontal",
  KeyF: "fibonacci",
  KeyR: "rectangle",
  KeyM: "measure",
};

interface OhlcBar {
  open: number;
  high: number;
  low: number;
  close: number;
}

function asOhlc(bar: object): OhlcBar | null {
  const b = bar as Partial<OhlcBar>;
  return typeof b.open === "number" && typeof b.high === "number" && typeof b.low === "number" && typeof b.close === "number"
    ? (b as OhlcBar)
    : null;
}

/** The bits of a mouse or touch event the takeover path needs. */
interface TakeoverEvt {
  shiftKey: boolean;
  preventDefault: () => void;
  stopPropagation: () => void;
}

function evtFromTouch(e: TouchEvent): TakeoverEvt {
  return { shiftKey: false, preventDefault: () => e.preventDefault(), stopPropagation: () => e.stopPropagation() };
}

function isTextInputTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
}

interface GroupMember {
  origin: Drawing;
  entry: ResolvedEntry;
}

interface DragState {
  hit: Hit;
  startX: number;
  startY: number;
  origin: Drawing;
  originEntry: ResolvedEntry;
  /** Other selected drawings that move with a body drag. */
  group: GroupMember[];
  moved: boolean;
}

interface PlacingState {
  p1: DataPoint;
  startX: number;
  startY: number;
}

export interface DrawingManagerOptions {
  chart: IChartApi;
  series: AnySeries;
  /** The element the chart was created in. */
  container: HTMLElement;
  /** Clicks inside this element (chart + its toolbars) make this chart the
   *  one keyboard shortcuts act on. Defaults to the container. */
  keyScope?: HTMLElement;
  /** Bar interval in seconds: anchors past the data step by it. */
  intervalSec: number;
  theme: DrawTheme;
  callbacks: DrawingCallbacks;
}

/**
 * TradingView-style editing for chart drawings.
 *
 * Placing: arm a tool, then click-move-click or press-drag-release, with a
 * live preview. Editing: hover highlights, drag a handle or the body (Shift =
 * 45° steps, magnet = snap to the candle's prices), Shift-click selects more
 * and a body drag moves them together, Delete removes, Escape cancels,
 * arrows nudge, Ctrl/Cmd+Z / Shift+Z undo / redo, Ctrl/Cmd+C / V / D copy,
 * paste and duplicate. Touch: bigger hit areas, drag, long-press to edit.
 * Pointer-downs that land on a drawing are stopped in the capture phase, so
 * the chart never pans (or selects a signal) underneath.
 */
export class DrawingManager {
  private readonly chart: IChartApi;
  private readonly series: AnySeries;
  private readonly container: HTMLElement;
  private readonly keyScope: HTMLElement;
  private readonly cb: DrawingCallbacks;
  private readonly primitive: DrawingsPrimitive;
  private readonly theme: DrawTheme;
  private readonly intervalSec: number;

  private tool: DrawingTool = "none";
  private drawings: Drawing[] = [];
  private placing: PlacingState | null = null;
  private drag: DragState | null = null;
  private selectedIds: string[] = [];
  private hoveredId: string | null = null;
  private magnet = false;
  /** Keyboard shortcuts act only on the chart clicked last. */
  private active = false;
  private longPress: { timer: number; startPos: Pt } | null = null;
  /** The measure tool's result: shown until the next click, never saved. */
  private measureResult: Drawing | null = null;
  private clipboard: Drawing[] = [];

  constructor(opts: DrawingManagerOptions) {
    this.chart = opts.chart;
    this.series = opts.series;
    this.container = opts.container;
    this.keyScope = opts.keyScope ?? opts.container;
    this.cb = opts.callbacks;
    this.theme = opts.theme;
    this.intervalSec = opts.intervalSec;
    this.primitive = new DrawingsPrimitive(opts.theme);
    this.primitive.setIntervalSec(opts.intervalSec);
    this.series.attachPrimitive(this.primitive);
    this.container.addEventListener("mousedown", this.handleMouseDown, true);
    this.container.addEventListener("dblclick", this.handleDblClick, true);
    this.container.addEventListener("contextmenu", this.handleContextMenu);
    this.container.addEventListener("touchstart", this.handleTouchStart, { capture: true, passive: false });
    window.addEventListener("mousedown", this.trackActive, true);
    window.addEventListener("touchstart", this.trackActive, { capture: true, passive: true });
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("mouseup", this.handleMouseUp);
    window.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    window.addEventListener("touchend", this.handleTouchEnd);
    window.addEventListener("touchcancel", this.handleTouchEnd);
    // Capture phase: runs before the full-screen Escape handler, which skips
    // an Escape this one already used.
    window.addEventListener("keydown", this.handleKeyDown, true);
  }

  destroy(): void {
    this.clearLongPress();
    this.container.removeEventListener("mousedown", this.handleMouseDown, true);
    this.container.removeEventListener("dblclick", this.handleDblClick, true);
    this.container.removeEventListener("contextmenu", this.handleContextMenu);
    this.container.removeEventListener("touchstart", this.handleTouchStart, true);
    window.removeEventListener("mousedown", this.trackActive, true);
    window.removeEventListener("touchstart", this.trackActive, true);
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
    window.removeEventListener("touchmove", this.handleTouchMove);
    window.removeEventListener("touchend", this.handleTouchEnd);
    window.removeEventListener("touchcancel", this.handleTouchEnd);
    window.removeEventListener("keydown", this.handleKeyDown, true);
    try {
      this.series.detachPrimitive(this.primitive);
    } catch {
      /* chart already removed */
    }
    this.container.style.cursor = "";
  }

  setTool(tool: DrawingTool): void {
    if (this.tool === tool) return;
    this.tool = tool;
    this.cancelPlacement();
    this.clearMeasure();
    if (tool !== "none") {
      this.active = true;
      this.select([]);
    }
    this.applyCursor(null);
  }

  setMagnet(on: boolean): void {
    this.magnet = on;
  }

  setRiskUsd(riskUsd: number): void {
    this.primitive.setRiskUsd(riskUsd);
  }

  /** Readouts keep out of the pane's right edge, where price-line titles sit. */
  setRightLane(px: number): void {
    this.primitive.setRightLane(px);
  }

  /** Select from outside (e.g. after an undo). Unknown ids are dropped. */
  setSelection(ids: string[]): void {
    this.select(ids.filter(id => this.drawings.some(d => d.id === id)));
  }

  setDrawings(drawings: Drawing[]): void {
    // Mid-drag the local copy is the truth; the change event at the end of
    // the drag delivers the final state.
    if (this.drag) return;
    this.drawings = drawings.map(d => ({ ...d }));
    const kept = this.selectedIds.filter(id => this.drawings.some(d => d.id === id));
    if (kept.length !== this.selectedIds.length) this.select(kept);
    this.primitive.setDrawings(this.drawings);
  }

  // ── Coordinates ────────────────────────────────────────────────────

  private ctx(): ResolveCtx {
    return makeResolveCtx(this.chart, this.series, this.intervalSec);
  }

  /** Pointer position in the price pane, or null outside it (RSI pane, axes). */
  private posFromClient(clientX: number, clientY: number): Pt | null {
    const rect = this.container.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const paneWidth = this.chart.timeScale().width();
    const paneHeight = this.chart.panes()[0]?.getHeight() ?? rect.height - this.chart.timeScale().height();
    if (paneWidth <= 0 || x < 0 || y < 0 || x > paneWidth || y > paneHeight) return null;
    return { x, y };
  }

  private toPoint(p: Pt): DataPoint | null {
    const time = xToTime(this.ctx(), p.x);
    const price = this.series.coordinateToPrice(p.y);
    if (time === null || price === null) return null;
    return { time, price };
  }

  private resolveAll(): ResolvedEntry[] {
    const ctx = this.ctx();
    return this.drawings.map(d => resolveEntry(d, ctx, "normal"));
  }

  private makeNew(tool: DrawingTool, p1: DataPoint, p2: DataPoint): Drawing {
    const id = crypto.randomUUID();
    const color = DEFAULT_DRAWING_COLOR;
    if (tool === "long-position" || tool === "short-position") {
      // Entry = first click; the second is the stop (where the idea is wrong,
      // on either side: its distance is the risk) and the target goes twice as
      // far, the system's 1:2 plan. Drag any of them afterwards.
      const entry = p1.price;
      const risk = Math.abs(p2.price - entry);
      const dir = tool === "long-position" ? 1 : -1;
      return {
        id,
        type: "position",
        side: dir === 1 ? "long" : "short",
        color,
        price: entry,
        time: Math.min(p1.time, p2.time),
        time2: Math.max(p1.time, p2.time),
        targetPrice: entry + dir * PLAN_REWARD * risk,
        stopPrice: entry - dir * risk,
      };
    }
    if (tool === "horizontal") return { id, color, type: "horizontal", price: p1.price };
    if (tool === "vertical") return { id, color, type: "vertical", price: p1.price, time: p1.time };
    if (tool === "text") return { id, color, type: "text", price: p1.price, time: p1.time, text: "Note" };
    const two = { id, color, price: p1.price, time: p1.time, price2: p2.price, time2: p2.time };
    if (tool === "ray") return { ...two, type: "trendline", extendRight: true };
    if (tool === "extended") return { ...two, type: "trendline", extendLeft: true, extendRight: true };
    if (tool === "measure") return { ...two, type: "trendline", color: this.theme.muted, lineStyle: "dashed", width: 1 };
    if (tool === "channel") {
      // The second line starts parallel, mirrored below, so it can be grabbed.
      return { ...two, type: "channel", time3: p1.time, price3: 2 * p1.price - p2.price };
    }
    if (tool === "none") return { ...two, type: "trendline" };
    return { ...two, type: tool };
  }

  // ── Mouse ──────────────────────────────────────────────────────────

  private trackActive = (e: Event): void => {
    this.active = e.target instanceof Node && this.keyScope.contains(e.target);
  };

  private handleMouseDown = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    const pos = this.posFromClient(e.clientX, e.clientY);
    if (!pos) return;
    this.clearMeasure();
    if (this.tool !== "none") {
      this.placementStart(pos, e);
      return;
    }
    this.selectionStart(pos, e, false);
  };

  private handleMouseMove = (e: MouseEvent): void => {
    const pos = this.posFromClient(e.clientX, e.clientY);
    if (this.drag) {
      if (pos) this.dragMove(pos, e.shiftKey);
      return;
    }
    if (!pos) {
      this.hoverHit(null);
      return;
    }
    if (this.placing) {
      this.placingMove(pos, e.shiftKey);
      return;
    }
    if (this.tool === "none") this.hoverHit(hitTest(this.resolveAll(), pos));
  };

  private handleMouseUp = (e: MouseEvent): void => {
    if (this.drag) {
      this.endDrag();
      return;
    }
    if (this.placing) this.maybeCommitPlacement(this.posFromClient(e.clientX, e.clientY), e.shiftKey);
  };

  private handleDblClick = (e: MouseEvent): void => {
    if (this.tool !== "none") return;
    const pos = this.posFromClient(e.clientX, e.clientY);
    if (!pos) return;
    const hit = hitTest(this.resolveAll(), pos);
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    this.select([hit.id]);
    this.cb.onRequestEdit?.(hit.id);
  };

  // Right-click on a drawing selects it (its style bar opens) instead of the
  // browser menu.
  private handleContextMenu = (e: MouseEvent): void => {
    const pos = this.posFromClient(e.clientX, e.clientY);
    if (!pos) return;
    const hit = hitTest(this.resolveAll(), pos);
    if (!hit) return;
    e.preventDefault();
    if (!this.selectedIds.includes(hit.id)) this.select([hit.id]);
  };

  // ── Touch ──────────────────────────────────────────────────────────

  private handleTouchStart = (e: TouchEvent): void => {
    if (e.touches.length !== 1) return; // pinch / two fingers → the chart
    const t = e.touches[0]!;
    const pos = this.posFromClient(t.clientX, t.clientY);
    if (!pos) return;
    this.clearMeasure();
    if (this.tool !== "none") {
      this.placementStart(pos, evtFromTouch(e));
      return;
    }
    const hit = this.selectionStart(pos, evtFromTouch(e), true);
    if (hit) this.startLongPress(hit.id, pos);
  };

  private handleTouchMove = (e: TouchEvent): void => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0]!;
    const pos = this.posFromClient(t.clientX, t.clientY);
    if (this.longPress && (!pos || dist(pos, this.longPress.startPos) > LONG_PRESS_CANCEL_PX)) this.clearLongPress();
    if (this.drag) {
      e.preventDefault(); // keep the page from scrolling mid-drag
      if (pos) this.dragMove(pos, false);
      return;
    }
    if (this.placing && pos) this.placingMove(pos, false);
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    this.clearLongPress();
    if (this.drag) {
      this.endDrag();
      return;
    }
    if (!this.placing) return;
    const t = e.changedTouches[0];
    this.maybeCommitPlacement(t ? this.posFromClient(t.clientX, t.clientY) : null, false);
  };

  private startLongPress(id: string, pos: Pt): void {
    this.clearLongPress();
    const timer = window.setTimeout(() => {
      this.longPress = null;
      this.drag = null; // a long-press edits instead of dragging
      this.applyCursor(null);
      this.cb.onRequestEdit?.(id);
    }, LONG_PRESS_MS);
    this.longPress = { timer, startPos: pos };
  }

  private clearLongPress(): void {
    if (!this.longPress) return;
    window.clearTimeout(this.longPress.timer);
    this.longPress = null;
  }

  // ── Keyboard ───────────────────────────────────────────────────────

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (!this.active || isTextInputTarget(e.target)) return;
    if (this.handleHistoryShortcut(e) || this.handleToolShortcut(e)) return;
    if (this.handleClipboardShortcut(e) || this.handleArrowNudge(e)) return;
    if (e.key === "Escape") {
      if (this.handleEscape()) e.preventDefault();
      return;
    }
    if ((e.key === "Delete" || e.key === "Backspace") && this.selectedIds.length > 0 && !this.drag) {
      e.preventDefault();
      this.removeSelected();
    }
  };

  private handleHistoryShortcut(e: KeyboardEvent): boolean {
    if (!e.ctrlKey && !e.metaKey) return false;
    if (e.code === "KeyZ") {
      e.preventDefault();
      if (e.shiftKey) this.cb.onRedo?.();
      else this.cb.onUndo?.();
      return true;
    }
    if (e.code === "KeyY") {
      e.preventDefault();
      this.cb.onRedo?.();
      return true;
    }
    return false;
  }

  private handleToolShortcut(e: KeyboardEvent): boolean {
    if (!e.altKey || e.ctrlKey || e.metaKey) return false;
    const tool = TOOL_SHORTCUTS[e.code];
    if (!tool) return false;
    e.preventDefault();
    this.cb.onSelectTool?.(tool);
    return true;
  }

  private handleClipboardShortcut(e: KeyboardEvent): boolean {
    if (!e.ctrlKey && !e.metaKey) return false;
    if (e.code === "KeyC" && this.selectedIds.length > 0) {
      this.copySelected();
      return true;
    }
    if (e.code === "KeyV" && this.clipboard.length > 0) {
      e.preventDefault();
      this.pasteClipboard();
      return true;
    }
    if (e.code === "KeyD" && this.selectedIds.length > 0) {
      e.preventDefault();
      this.copySelected();
      this.pasteClipboard();
      return true;
    }
    return false;
  }

  private handleArrowNudge(e: KeyboardEvent): boolean {
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const d = deltas[e.key];
    if (!d || this.selectedIds.length === 0) return false;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    this.nudgeSelected(d[0] * step, d[1] * step);
    return true;
  }

  private copySelected(): void {
    this.clipboard = this.drawings.filter(d => this.selectedIds.includes(d.id)).map(d => ({ ...d }));
  }

  private pasteClipboard(): void {
    const dt = this.intervalSec * 3; // a few bars to the right
    const created = this.clipboard.map(d => ({
      ...d,
      id: crypto.randomUUID(),
      time: d.time != null ? d.time + dt : undefined,
      time2: d.time2 != null ? d.time2 + dt : undefined,
      time3: d.time3 != null ? d.time3 + dt : undefined,
    }));
    this.drawings = [...this.drawings, ...created];
    this.primitive.setDrawings(this.drawings);
    this.emit({ added: created });
    this.select(created.map(c => c.id));
  }

  private nudgeSelected(dx: number, dy: number): void {
    const entries = this.resolveAll();
    const updates: Drawing[] = [];
    for (const id of this.selectedIds) {
      const origin = this.drawings.find(d => d.id === id);
      const entry = entries.find(en => en.d.id === id);
      if (!origin || !entry || origin.locked) continue;
      const u = this.shiftDrawing(origin, entry, dx, dy);
      if (u) updates.push(u);
    }
    if (updates.length === 0) return;
    this.applyUpdates(updates);
    this.emit({ updated: updates });
  }

  /** Escape: drop the armed tool, else the measurement, else the selection. */
  private handleEscape(): boolean {
    if (this.tool !== "none") {
      this.tool = "none";
      this.cancelPlacement();
      this.applyCursor(null);
      this.cb.onToolFinished();
      return true;
    }
    if (this.measureResult) {
      this.clearMeasure();
      return true;
    }
    if (this.selectedIds.length > 0) {
      this.select([]);
      return true;
    }
    return false;
  }

  // ── Placing ────────────────────────────────────────────────────────

  private placementStart(pos: Pt, e: TakeoverEvt): void {
    const pt = this.pointFor(pos, e.shiftKey, this.placementAnchorPx());
    if (!pt) return;
    // Take the event over so the chart doesn't pan while drawing.
    e.preventDefault();
    e.stopPropagation();
    if (this.tool === "horizontal" || this.tool === "vertical" || this.tool === "text") {
      const d = this.makeNew(this.tool, pt, pt);
      this.commitDrawing(d);
      if (d.type === "text") this.cb.onRequestEdit?.(d.id);
      return;
    }
    if (this.placing) {
      this.commitPlacement(this.placing.p1, pt);
      return;
    }
    this.placing = { p1: pt, startX: pos.x, startY: pos.y };
    this.primitive.setPreview(this.makeNew(this.tool, pt, pt));
  }

  private commitPlacement(p1: DataPoint, p2: DataPoint): void {
    if (this.tool === "measure") {
      this.finishMeasure(p1, p2);
      return;
    }
    this.commitDrawing(this.makeNew(this.tool, p1, p2));
  }

  private finishMeasure(p1: DataPoint, p2: DataPoint): void {
    this.placing = null;
    this.measureResult = { ...this.makeNew("measure", p1, p2), id: "measure" };
    this.primitive.setPreview(this.measureResult);
    this.tool = "none";
    this.applyCursor(null);
    this.cb.onToolFinished();
  }

  private clearMeasure(): void {
    if (!this.measureResult) return;
    this.measureResult = null;
    this.primitive.setPreview(null);
  }

  private placingMove(pos: Pt, shiftKey: boolean): void {
    const pt = this.pointFor(pos, shiftKey, this.placementAnchorPx());
    if (!pt || !this.placing) return;
    this.primitive.setPreview(this.makeNew(this.tool, this.placing.p1, pt));
  }

  private maybeCommitPlacement(pos: Pt | null, shiftKey: boolean): void {
    const placing = this.placing!;
    if (!pos || dist(pos, { x: placing.startX, y: placing.startY }) < DRAG_COMMIT_THRESHOLD_PX) return;
    const pt = this.pointFor(pos, shiftKey, this.placementAnchorPx());
    if (pt) this.commitPlacement(placing.p1, pt);
  }

  private commitDrawing(d: Drawing): void {
    this.placing = null;
    this.primitive.setPreview(null);
    this.drawings = [...this.drawings, d];
    this.primitive.setDrawings(this.drawings);
    this.emit({ added: [d] });
    this.select([d.id]);
    this.tool = "none";
    this.applyCursor(null);
    this.cb.onToolFinished();
  }

  private cancelPlacement(): void {
    this.placing = null;
    this.primitive.setPreview(null);
  }

  // ── Selecting & dragging ───────────────────────────────────────────

  private selectionStart(pos: Pt, e: TakeoverEvt, isTouch: boolean): Hit | null {
    const entries = this.resolveAll();
    const hit = hitTest(entries, pos, isTouch ? TOUCH_HIT_SCALE : 1);
    if (!hit) {
      if (!e.shiftKey) this.select([]);
      return null;
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.shiftKey) {
      this.select(
        this.selectedIds.includes(hit.id) ? this.selectedIds.filter(x => x !== hit.id) : [...this.selectedIds, hit.id],
      );
      return hit;
    }
    if (!this.selectedIds.includes(hit.id)) this.select([hit.id]);
    this.beginDrag(hit, pos, entries);
    return hit;
  }

  private beginDrag(hit: Hit, pos: Pt, entries: ResolvedEntry[]): void {
    const origin = this.drawings.find(d => d.id === hit.id);
    const originEntry = entries.find(en => en.d.id === hit.id);
    // Locked drawings select (so they can be unlocked) but never move.
    if (!origin || !originEntry || origin.locked) return;
    const group: GroupMember[] = [];
    if (hit.region.kind === "body" && this.selectedIds.length > 1) {
      for (const id of this.selectedIds) {
        if (id === hit.id) continue;
        const o = this.drawings.find(d => d.id === id);
        const en = entries.find(x => x.d.id === id);
        if (o && en && !o.locked) group.push({ origin: { ...o }, entry: en });
      }
    }
    this.drag = { hit, startX: pos.x, startY: pos.y, origin: { ...origin }, originEntry, group, moved: false };
    this.applyCursor("grabbing");
  }

  private dragMove(pos: Pt, shiftKey: boolean): void {
    const drag = this.drag!;
    const updates = this.computeDragUpdates(drag, pos, shiftKey);
    if (updates.length === 0) return;
    drag.moved = true;
    this.applyUpdates(updates);
  }

  private computeDragUpdates(drag: DragState, pos: Pt, shiftKey: boolean): Drawing[] {
    if (drag.hit.region.kind === "point") {
      const pt = this.pointFor(pos, shiftKey, this.dragAnchorPx(drag));
      if (!pt) return [];
      const updated: Drawing = { ...drag.origin };
      const { timeKey, priceKey } = drag.hit.region;
      if (timeKey) updated[timeKey] = pt.time;
      if (priceKey) updated[priceKey] = pt.price;
      return [updated];
    }
    const dx = pos.x - drag.startX;
    const dy = pos.y - drag.startY;
    const primary = this.shiftDrawing(drag.origin, drag.originEntry, dx, dy);
    if (!primary) return [];
    const updates = [primary];
    for (const g of drag.group) {
      const u = this.shiftDrawing(g.origin, g.entry, dx, dy);
      if (u) updates.push(u);
    }
    return updates;
  }

  /** Move a whole drawing by a pixel delta. */
  private shiftDrawing(origin: Drawing, entry: ResolvedEntry, dx: number, dy: number): Drawing | null {
    if (origin.type === "horizontal") {
      if (entry.y1 === null) return null;
      const price = this.series.coordinateToPrice(entry.y1 + dy);
      return price === null ? null : { ...origin, price };
    }
    if (origin.type === "vertical") {
      const p = this.shiftPoint(entry.x1, entry.y1, dx, 0);
      return p ? { ...origin, time: p.time } : null;
    }
    if (origin.type === "position") {
      // Shift the box in time and every price row together.
      const p = this.shiftPoint(entry.x1, entry.y1, dx, dy);
      if (!p) return null;
      const dPrice = p.price - origin.price;
      const dTime = p.time - (origin.time ?? p.time);
      return {
        ...origin,
        time: (origin.time ?? 0) + dTime,
        time2: (origin.time2 ?? 0) + dTime,
        price: p.price,
        stopPrice: origin.stopPrice != null ? origin.stopPrice + dPrice : undefined,
        targetPrice: origin.targetPrice != null ? origin.targetPrice + dPrice : undefined,
      };
    }
    const p1 = this.shiftPoint(entry.x1, entry.y1, dx, dy);
    const p2 = this.shiftPoint(entry.x2, entry.y2, dx, dy);
    if (!p1 || !p2) return null;
    const moved = { ...origin, time: p1.time, price: p1.price, time2: p2.time, price2: p2.price };
    if (origin.type !== "channel") return moved;
    const p3 = this.shiftPoint(entry.x1, entry.y3 ?? entry.y1, dx, dy);
    return p3 ? { ...moved, time3: p3.time, price3: p3.price } : null;
  }

  private shiftPoint(x: number | null, y: number | null, dx: number, dy: number): DataPoint | null {
    if (x === null || y === null) return null;
    return this.toPoint({ x: x + dx, y: y + dy });
  }

  private endDrag(): void {
    const drag = this.drag!;
    this.drag = null;
    this.applyCursor(null);
    if (!drag.moved) return;
    const ids = new Set([drag.hit.id, ...drag.group.map(g => g.origin.id)]);
    this.emit({ updated: this.drawings.filter(d => ids.has(d.id)).map(d => ({ ...d })) });
  }

  // ── Snapping ───────────────────────────────────────────────────────

  /**
   * Cursor → data point: onto another drawing's anchor when close, else
   * Shift = 45° steps (trendlines), else the magnet.
   */
  private pointFor(pos: Pt, shiftKey: boolean, angleAnchor: Pt | null): DataPoint | null {
    const snapped = this.snapToNearbyAnchor(pos);
    if (snapped) return snapped;
    if (shiftKey && angleAnchor) return this.toPoint(snapAngle(angleAnchor, pos));
    const pt = this.toPoint(pos);
    if (!pt || !this.magnet) return pt;
    return this.magnetSnap(pt, pos.y);
  }

  // The selected drawings are skipped, so a drag never snaps to itself.
  private snapToNearbyAnchor(pos: Pt): DataPoint | null {
    for (const e of this.resolveAll()) {
      if (this.selectedIds.includes(e.d.id)) continue;
      const anchors: Array<[number | null | undefined, number | null | undefined, number | undefined, number | undefined]> = [
        [e.x1, e.y1, e.d.time, e.d.price],
        [e.x2, e.y2, e.d.time2, e.d.price2],
        [e.x3, e.y3, e.d.time3, e.d.price3],
      ];
      for (const [x, y, time, price] of anchors) {
        if (x == null || y == null || time == null || price == null) continue;
        if (dist(pos, { x, y }) <= SNAP_ANCHOR_PX) return { time, price };
      }
    }
    return null;
  }

  /** The first anchor in px while placing a trendline (Shift snaps around it). */
  private placementAnchorPx(): Pt | null {
    if (!this.placing || this.tool !== "trendline") return null;
    const x = timeToX(this.ctx(), this.placing.p1.time);
    const y = this.series.priceToCoordinate(this.placing.p1.price);
    return x !== null && y !== null ? { x, y } : null;
  }

  /** The anchor opposite the one being dragged, in px. */
  private dragAnchorPx(drag: DragState): Pt | null {
    if (drag.origin.type !== "trendline" || drag.hit.region.kind !== "point") return null;
    const en = drag.originEntry;
    const movingP1 = drag.hit.region.timeKey === "time";
    const x = movingP1 ? en.x2 : en.x1;
    const y = movingP1 ? en.y2 : en.y1;
    return x !== null && y !== null ? { x, y } : null;
  }

  private magnetSnap(pt: DataPoint, cursorY: number): DataPoint {
    const candle = this.candleAt(pt.time);
    if (!candle) return pt;
    let bestPrice = pt.price;
    let bestDist = MAGNET_THRESHOLD_PX;
    for (const price of [candle.open, candle.high, candle.low, candle.close]) {
      const y = this.series.priceToCoordinate(price);
      if (y === null) continue;
      const d = Math.abs(y - cursorY);
      if (d < bestDist) {
        bestDist = d;
        bestPrice = price;
      }
    }
    return { time: pt.time, price: bestPrice };
  }

  private candleAt(time: number): OhlcBar | null {
    const data = this.series.data();
    let lo = 0;
    let hi = data.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const t = data[mid]!.time as number;
      if (t === time) return asOhlc(data[mid]!);
      if (t < time) lo = mid + 1;
      else hi = mid - 1;
    }
    return null;
  }

  // ── State ──────────────────────────────────────────────────────────

  private applyUpdates(updates: Drawing[]): void {
    const byId = new Map(updates.map(u => [u.id, u]));
    this.drawings = this.drawings.map(d => byId.get(d.id) ?? d);
    this.primitive.setDrawings(this.drawings);
  }

  private emit(change: DrawingChange): void {
    this.cb.onChange(change);
  }

  private removeSelected(): void {
    const ids = this.selectedIds;
    this.select([]);
    this.drawings = this.drawings.filter(d => !ids.includes(d.id));
    this.primitive.setDrawings(this.drawings);
    this.emit({ removed: [...ids] });
  }

  private select(ids: string[]): void {
    if (ids.length === this.selectedIds.length && ids.every((id, i) => id === this.selectedIds[i])) return;
    this.selectedIds = ids;
    this.primitive.setSelected(ids);
    this.cb.onSelectionChange([...ids]);
  }

  private hoverHit(hit: Hit | null): void {
    const id = hit?.id ?? null;
    if (id !== this.hoveredId) {
      this.hoveredId = id;
      this.primitive.setHovered(id);
    }
    this.applyCursor(hit ? "pointer" : null);
  }

  private applyCursor(cursor: "pointer" | "grabbing" | null): void {
    this.container.style.cursor = cursor ?? (this.tool !== "none" ? "crosshair" : "");
  }
}
